import {
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	useReactTable,
	VisibilityState,
} from "@tanstack/react-table";
import { IEntity } from "domain/Domain";
import { useEffect, useMemo, useState } from "react";
import { SBTableColumn } from "../../models/TableColumn";
import { columnsAdapter } from "../helpers/columnsAdapter";
import useTableExpanded from "./useTableExpanded";
import useTableFilter from "./useTableFilter";
import useTableGrouping from "./useTableGrouping";
import useTableSelection from "./useTableSelection";
import useTableSorting from "./useTableSorting";

export default <T extends IEntity>(props: useTableManagerProps<T>) => {
	const {
		defaultFilter,
		startRows,
		defaultColumns,
		onSelect,
		hasSelection,
		onDataUpdated,
		collapseFieldName,
	} = props;

	const [data, setData] = useState(() => startRows);

	useEffect(() => {
		setData(startRows);
	}, [startRows]);

	const {
		filter,
		fuzzyFilter,
		setGlobalFilter,
		columnFilters,
		setColumnFilters,
		filterFunction,
		setExternalFilter,
	} = useTableFilter({ defaultColumns, defaultFilter });

	const { sorting, setSorting } = useTableSorting();

	const { grouping, setGrouping } = useTableGrouping();

	const { expanded, setExpanded } = useTableExpanded();

	const { rowSelection, setRowSelection } = useTableSelection({
		onSelect,
		data,
	});

	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		() =>
			Object.fromEntries(
				(defaultColumns ?? [])
					.flatMap((c) => c.headers ?? [])
					.filter((h) => h.tableHide && h.key)
					.map((h) => [h.key!, false]),
			),
	);

	const columns = useMemo(() => {
		const cachedColumns = defaultColumns;

		const newColumns = columnsAdapter<T>({
			columns: cachedColumns,
			rows: startRows,
			hasSelection,
		});

		return newColumns;
	}, []);

	const table = useReactTable<T>({
		data,
		columns,
		filterFns: {
			fuzzy: fuzzyFilter,
		},
		state: {
			columnFilters,
			sorting,
			grouping,
			expanded,
			columnVisibility,
			rowSelection,
			globalFilter: filter,
		},
		onGlobalFilterChange: setGlobalFilter,
		onColumnFiltersChange: setColumnFilters,
		globalFilterFn: filterFunction,
		onColumnVisibilityChange: setColumnVisibility,
		onSortingChange: setSorting,
		onGroupingChange: setGrouping,
		onExpandedChange: setExpanded,
		onRowSelectionChange: setRowSelection,
		getFilteredRowModel: getFilteredRowModel(),
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSubRows: (row: any) => {
			if (!collapseFieldName) return undefined;
			const subRows = row[collapseFieldName];
			const hasSubRows = Array.isArray(subRows) && subRows.length > 0;
			return hasSubRows ? subRows : undefined;
		},
		autoResetExpanded: false,
		meta: {
			updateData: (
				rowId: string | number,
				columnId: string,
				value: number | string,
			) => {
				setData((old) => {
					const newRows = [...old];
					const path = String(rowId).split(".");

					if (path.length === 1) {
						const index = parseInt(path[0], 10);
						newRows[index] = {
							...newRows[index]!,
							[columnId]: value,
						};
					} else if (path.length === 2 && collapseFieldName) {
						const parentIndex = parseInt(path[0], 10);
						const childIndex = parseInt(path[1], 10);

						const parent = { ...newRows[parentIndex]! };
						const children = [
							...((parent as any)[collapseFieldName] as any[]),
						];

						children[childIndex] = {
							...children[childIndex],
							[columnId]: value,
						};

						(parent as any)[collapseFieldName] = children;
						newRows[parentIndex] = parent;
					}

					return newRows;
				});
			},
		},
	});

	const { rows } = table.getRowModel();

	const toUpdateRefs = [
		data,
		columnFilters,
		sorting,
		grouping,
		expanded,
		columnVisibility,
		rowSelection,
		filter,
	];

	useEffect(() => {
		onDataUpdated?.(data);
	}, [data]);

	return {
		manager: {
			toUpdateRefs,
			table,
			rows,
			columns: table.getAllColumns(),
		},
		selection: {
			rowSelection,
		},
		filter: {
			setGlobalFilter,
			setExternalFilter,
			setColumnFilters,
		},
	};
};

interface useTableManagerProps<T extends IEntity> {
	defaultColumns: SBTableColumn<T>[];
	startRows: T[];
	hasSelection?: boolean;
	defaultFilter?: { id: string; value: any }[];
	onDataUpdated: any;
	onSelect?: (entities: T[], selections: any) => void;
	collapseFieldName?: string;
}
